import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Shield, Users, X, Edit3 } from "lucide-react";

const TABS = [["roles", "Roles"], ["permissions", "Permissions"], ["users", "User Assignments"]];

export default function RbacAdmin() {
  const [tab, setTab] = useState("roles");
  return (
    <div data-testid="rbac-admin">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-[#4a4a4a]"><Shield size={14} /> Role-Based Access Control</div>
      </div>
      <div className="border-b border-[#e5e5e5] flex gap-1 mb-6">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`rbac-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest -mb-px border-b-2 ${tab === k ? "border-[#002fa7] text-[#002fa7]" : "border-transparent text-[#4a4a4a]"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "roles" && <RolesTab />}
      {tab === "permissions" && <PermissionsTab />}
      {tab === "users" && <UsersTab />}
    </div>
  );
}

function RolesTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data } = useQuery({ queryKey: ["rbac-roles", q], queryFn: async () => (await api.get("/rbac/roles", { params: { q: q || undefined } })).data });
  const { data: perms } = useQuery({ queryKey: ["rbac-perms"], queryFn: async () => (await api.get("/rbac/permissions")).data });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/rbac/roles/${id}`)).data,
    onSuccess: () => { toast.success("Role deleted"); qc.invalidateQueries({ queryKey: ["rbac-roles"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <input placeholder="Search roles…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-[#0a0a0a] px-3 py-2 text-sm w-56" data-testid="roles-search" />
        <button onClick={() => setCreating(true)} className="rh-btn-primary inline-flex items-center gap-2 text-xs" data-testid="new-role-btn"><Plus size={14} /> New Role</button>
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-4">Role</div><div className="col-span-2">Type</div><div className="col-span-4">Permissions</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {(data?.items || []).map((r) => (
          <div key={r.role_id} className="grid grid-cols-12 gap-2 px-3 py-3 border-b border-[#e5e5e5] items-center text-sm" data-testid={`role-row-${r.role_id}`}>
            <div className="col-span-4">
              <div className="font-semibold">{r.name}</div>
              <div className="text-[10px] font-mono text-[#4a4a4a]">{r.role_id}</div>
              {r.description && <div className="text-xs text-[#4a4a4a] mt-1">{r.description}</div>}
            </div>
            <div className="col-span-2 text-xs">
              {r.system ? <span className="bg-[#002fa7] text-white px-2 py-1 text-[10px] uppercase tracking-widest">System</span> : <span className="bg-[#f5f5f5] px-2 py-1 text-[10px] uppercase tracking-widest">Custom</span>}
              {r.protected && <div className="text-[10px] text-[#ff3b30] mt-1">Protected</div>}
            </div>
            <div className="col-span-4 text-xs">
              {r.permissions.includes("*") ? <span className="font-mono text-[#002fa7] font-bold">* (all)</span> :
                <span className="font-mono text-[10px]">{r.permissions.length} permissions</span>}
            </div>
            <div className="col-span-2 flex justify-end gap-1">
              <button onClick={() => setEditing(r)} className="p-1 hover:bg-[#f5f5f5]" data-testid={`role-edit-${r.role_id}`}><Edit3 size={12} /></button>
              {!r.protected && <button onClick={() => { if (window.confirm(`Delete ${r.name}?`)) del.mutate(r.role_id); }} className="p-1 hover:bg-[#ff3b30] hover:text-white" data-testid={`role-delete-${r.role_id}`}><Trash2 size={12} /></button>}
            </div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No roles match.</div>}
      </div>
      {(creating || editing) && <RoleEditor role={editing} perms={perms} onClose={() => { setCreating(false); setEditing(null); }} />}
    </div>
  );
}

function RoleEditor({ role, perms, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [selected, setSelected] = useState(new Set(role?.permissions || []));

  const save = useMutation({
    mutationFn: async () => {
      const body = { name, description, permissions: Array.from(selected) };
      if (role) return (await api.put(`/rbac/roles/${role.role_id}`, body)).data;
      return (await api.post("/rbac/roles", body)).data;
    },
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["rbac-roles"] }); onClose(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const toggle = (p) => { const s = new Set(selected); s.has(p) ? s.delete(p) : s.add(p); setSelected(s); };
  const isSuper = selected.has("*");

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#0a0a0a] w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#0a0a0a] sticky top-0 bg-white">
          <h3 className="font-display font-bold text-xl">{role ? "Edit Role" : "New Role"}{role?.protected && <span className="ml-2 text-xs text-[#ff3b30]">(protected)</span>}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block"><span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" data-testid="role-name-input" /></label>
          <label className="block"><span className="block text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full border border-[#0a0a0a] px-3 py-2 text-sm" data-testid="role-desc-input" /></label>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">Permissions ({selected.size} selected)</div>
            {isSuper && <div className="mb-3 bg-[#002fa7] text-white p-2 text-xs">Wildcard <code>*</code> is set — this role has all permissions.</div>}
            {perms && Object.entries(perms.grouped).map(([group, items]) => (
              <div key={group} className="mb-3 border border-[#e5e5e5] p-3">
                <div className="font-semibold text-xs uppercase tracking-widest text-[#4a4a4a] mb-2">{group}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {items.map((p) => (
                    <label key={p.permission_id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`perm-${p.permission_id}`}>
                      <input type="checkbox" checked={selected.has(p.permission_id)} onChange={() => toggle(p.permission_id)} disabled={role?.protected && role?.permissions?.includes("*")} />
                      <span className="font-mono">{p.permission_id}</span>
                      <span className="text-[#4a4a4a]">— {p.description}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e5e5e5] sticky bottom-0 bg-white">
            <button onClick={onClose} className="rh-btn-secondary text-xs">Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending || !name} className="rh-btn-primary text-xs" data-testid="role-save-btn">{save.isPending ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionsTab() {
  const { data } = useQuery({ queryKey: ["rbac-perms"], queryFn: async () => (await api.get("/rbac/permissions")).data });
  return (
    <div data-testid="perms-tab">
      <div className="text-sm text-[#4a4a4a] mb-4">{data?.total || 0} permissions across {Object.keys(data?.grouped || {}).length} groups</div>
      {data && Object.entries(data.grouped).map(([g, items]) => (
        <div key={g} className="mb-4 border border-[#0a0a0a] p-4" data-testid={`perm-group-${g}`}>
          <div className="rh-overline mb-3">{g}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {items.map((p) => (
              <div key={p.permission_id} className="text-sm flex items-baseline gap-3 border-b border-[#e5e5e5] pb-1">
                <span className="font-mono text-xs font-semibold w-48 flex-shrink-0">{p.permission_id}</span>
                <span className="text-[#4a4a4a] text-xs">{p.description}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [assigning, setAssigning] = useState(null);

  const { data } = useQuery({ queryKey: ["rbac-users", q], queryFn: async () => (await api.get("/rbac/users", { params: { q: q || undefined } })).data });
  const { data: roles } = useQuery({ queryKey: ["rbac-roles"], queryFn: async () => (await api.get("/rbac/roles")).data });

  const assign = useMutation({
    mutationFn: async ({ user_id, role_id }) => (await api.post(`/rbac/users/${user_id}/roles`, { role_id })).data,
    onSuccess: () => { toast.success("Role assigned"); qc.invalidateQueries({ queryKey: ["rbac-users"] }); setAssigning(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: async ({ user_id, role_id }) => (await api.delete(`/rbac/users/${user_id}/roles/${role_id}`)).data,
    onSuccess: () => { toast.success("Role removed"); qc.invalidateQueries({ queryKey: ["rbac-users"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const roleMap = useMemo(() => Object.fromEntries((roles?.items || []).map(r => [r.role_id, r.name])), [roles]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <input placeholder="Search users by email/name…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-[#0a0a0a] px-3 py-2 text-sm w-72" data-testid="users-search" />
        <div className="flex items-center gap-1 text-xs text-[#4a4a4a]"><Users size={12} /> {data?.total || 0} users</div>
      </div>
      <div className="border border-[#e5e5e5]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#e5e5e5] bg-[#f5f5f5] text-[10px] font-mono uppercase tracking-widest text-[#4a4a4a]">
          <div className="col-span-4">User</div><div className="col-span-2">Role Flag</div><div className="col-span-4">Assigned Roles</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {(data?.items || []).map((u) => (
          <div key={u.user_id} className="grid grid-cols-12 gap-2 px-3 py-3 border-b border-[#e5e5e5] items-center text-sm" data-testid={`user-row-${u.user_id}`}>
            <div className="col-span-4"><div className="font-semibold text-xs">{u.name}</div><div className="text-[10px] text-[#4a4a4a]">{u.email}</div></div>
            <div className="col-span-2 text-xs"><span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${u.role === "admin" ? "bg-[#002fa7] text-white" : "bg-[#f5f5f5]"}`}>{u.role || "customer"}</span></div>
            <div className="col-span-4 flex flex-wrap gap-1">
              {(u.role_ids || []).length === 0 ? <span className="text-xs text-[#4a4a4a]">—</span> :
                u.role_ids.map((rid) => (
                  <span key={rid} className="inline-flex items-center gap-1 border border-[#e5e5e5] px-2 py-0.5 text-[10px]" data-testid={`user-role-${u.user_id}-${rid}`}>
                    {roleMap[rid] || rid}
                    <button onClick={() => remove.mutate({ user_id: u.user_id, role_id: rid })} className="text-[#4a4a4a] hover:text-[#ff3b30]"><X size={10} /></button>
                  </span>
                ))
              }
            </div>
            <div className="col-span-2 text-right">
              <button onClick={() => setAssigning(u)} className="rh-btn-secondary text-xs" data-testid={`user-assign-${u.user_id}`}>Assign Role</button>
            </div>
          </div>
        ))}
        {(data?.items || []).length === 0 && <div className="p-8 text-center text-[#4a4a4a] text-sm">No users.</div>}
      </div>
      {assigning && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setAssigning(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#0a0a0a] w-full max-w-md">
            <div className="flex justify-between items-center px-4 py-3 border-b border-[#0a0a0a]">
              <h3 className="font-display font-bold">Assign role to {assigning.name}</h3>
              <button onClick={() => setAssigning(null)}><X size={16} /></button>
            </div>
            <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
              {(roles?.items || []).filter((r) => !assigning.role_ids?.includes(r.role_id)).map((r) => (
                <button key={r.role_id} onClick={() => assign.mutate({ user_id: assigning.user_id, role_id: r.role_id })} className="w-full text-left border border-[#e5e5e5] hover:border-[#002fa7] p-3" data-testid={`assign-choice-${r.role_id}`}>
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-xs text-[#4a4a4a]">{r.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
